import React from 'react'
import { FileText, HelpCircle, Target, ListChecks } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// The Module 2 coach welcome message is persisted as a JSON string with these fields. It is rendered as a
// structured card in the live chat and in the chat-history viewer (ChatDetail). Anything that displays it as
// raw text shows an ugly JSON blob, so always parse with parseCoachMessage and render with CoachMessageCard.
export interface CoachMessagePayload {
  message?: string
  welcomeMessage?: string
  rolePlayScenario?: string
  rolePlaySubtitle?: string
  keyQuestion?: string
  rolePlayGoal?: string
  objectives?: string[]
  callToAction?: string
}

export function parseCoachMessage(content: string): CoachMessagePayload | null {
  try {
    const parsed = JSON.parse(content)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    const knownFields = ['message', 'welcomeMessage', 'rolePlayScenario', 'rolePlaySubtitle', 'keyQuestion', 'rolePlayGoal', 'objectives', 'callToAction']
    if (!knownFields.some((f) => f in parsed)) return null
    return {
      message: typeof parsed.message === 'string' ? parsed.message : undefined,
      welcomeMessage: typeof parsed.welcomeMessage === 'string' ? parsed.welcomeMessage : undefined,
      rolePlayScenario: typeof parsed.rolePlayScenario === 'string' ? parsed.rolePlayScenario : undefined,
      rolePlaySubtitle: typeof parsed.rolePlaySubtitle === 'string' ? parsed.rolePlaySubtitle : undefined,
      keyQuestion: typeof parsed.keyQuestion === 'string' ? parsed.keyQuestion : undefined,
      rolePlayGoal: typeof parsed.rolePlayGoal === 'string' ? parsed.rolePlayGoal : undefined,
      objectives: Array.isArray(parsed.objectives) ? parsed.objectives.filter((o: unknown): o is string => typeof o === 'string') : undefined,
      callToAction: typeof parsed.callToAction === 'string' ? parsed.callToAction : undefined,
    }
  } catch {
    return null
  }
}

// Coach welcome bubble — white assistant bubble with markdown body.
const CoachBubble: React.FC<{ content: string }> = ({ content }) => (
  <div className="flex justify-start mb-4">
    <div className="max-w-[85%] sm:max-w-[75%] rounded-lg px-3 py-2 sm:px-4 bg-white text-gray-800 border border-gray-200">
      <div className="prose prose-sm max-w-none dark:prose-invert wrap-break-word text-gray-800">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ hr: (props) => <hr {...props} className="not-prose my-3" /> }}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  </div>
)

const CoachSection: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--m2-brand)' }}>
      <span style={{ color: 'var(--m2-brand)' }}>{icon}</span>
      {label}
    </div>
    <div className="prose prose-sm max-w-none text-gray-700 wrap-break-word">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(children)}</ReactMarkdown>
    </div>
  </div>
)

// Scenario brief — the bordered middle bubble (scenario + key question + goal + objectives)
const CoachScenarioCard: React.FC<{ payload: CoachMessagePayload }> = ({ payload }) => {
  const hasObjectives = payload.objectives && payload.objectives.length > 0
  const hasBody = Boolean(payload.rolePlayScenario) || Boolean(payload.rolePlayGoal) || hasObjectives || Boolean(payload.keyQuestion)
  if (!hasBody) return null

  return (
    <div className="flex justify-start mb-4">
      <div className="max-w-[85%] sm:max-w-[75%] rounded-xl overflow-hidden shadow-sm border" style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}>
        {/* Panel header — the mockup's "Role play scenario" block head */}
        {payload.rolePlayScenario && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b" style={{ background: '#FCFDFB', borderColor: 'var(--m2-line)' }}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--m2-ink)' }}>
              <FileText size={14} style={{ color: 'var(--m2-brand)' }} />
              Role Play Scenario
            </div>
            {payload.rolePlaySubtitle && (
              <div className="text-[11px] text-right shrink-0" style={{ color: 'var(--m2-muted)' }}>
                {payload.rolePlaySubtitle}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4 px-4 pb-4 pt-4">
          {payload.rolePlayScenario && (
            <div className="prose prose-sm max-w-none text-gray-700 wrap-break-word">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{payload.rolePlayScenario}</ReactMarkdown>
            </div>
          )}

          {payload.keyQuestion && (
            <div className="rounded-r-md py-2.5 px-3.5" style={{ background: 'var(--m2-mint)', borderLeft: '3px solid var(--m2-brand)' }}>
              <div className="flex items-center gap-1.5 text-[9.5px] font-bold tracking-[1.5px] mb-1" style={{ color: 'var(--m2-brand)' }}>
                <HelpCircle size={12} />
                KEY QUESTION
              </div>
              <div className="text-[12.5px] italic leading-snug" style={{ color: 'var(--m2-brand)' }}>
                {payload.keyQuestion}
              </div>
            </div>
          )}

          {payload.rolePlayGoal && (
            <CoachSection icon={<Target size={13} />} label="Role Play Goal">
              {payload.rolePlayGoal}
            </CoachSection>
          )}

          {hasObjectives && (
            <div>
              <div className="flex items-center gap-1.5 mb-2 font-semibold text-xs uppercase tracking-wide" style={{ color: 'var(--m2-brand)' }}>
                <ListChecks size={13} />
                Objectives
              </div>
              <div className="grid grid-cols-2 gap-2">
                {payload.objectives!.map((obj, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md px-2.5 py-1.5" style={{ background: 'var(--m2-line-soft)' }}>
                    <span
                      className="flex-shrink-0 w-[18px] h-[18px] rounded-full text-[9.5px] font-bold flex items-center justify-center"
                      style={{ background: 'var(--m2-brand)', color: 'var(--m2-lime)' }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-[12.5px] leading-snug" style={{ color: 'var(--m2-ink)' }}>
                      {obj}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Skeleton placeholder shown while the first coach message (the welcome) is being generated. Mirrors
// the CoachMessageCard layout — welcome bubble, scenario brief card, call-to-action bubble — so the
// real content settles into the same shape without a jarring swap.
export const CoachWelcomeSkeleton: React.FC = () => (
  <div className="animate-pulse" aria-hidden="true">
    {/* welcome bubble */}
    <div className="flex justify-start mb-4">
      <div className="w-[85%] sm:w-[75%] rounded-lg px-3 py-2.5 sm:px-4 bg-white border border-gray-200 space-y-2">
        <div className="h-3 w-1/3 rounded bg-gray-200" />
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-4/5 rounded bg-gray-200" />
      </div>
    </div>

    {/* scenario brief card */}
    <div className="flex justify-start mb-4">
      <div className="w-[85%] sm:w-[75%] rounded-xl overflow-hidden shadow-sm border" style={{ background: 'var(--m2-card)', borderColor: 'var(--m2-line)' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ background: '#FCFDFB', borderColor: 'var(--m2-line)' }}>
          <div className="h-3.5 w-3.5 rounded bg-gray-200" />
          <div className="h-3 w-32 rounded bg-gray-200" />
        </div>
        <div className="flex flex-col gap-4 px-4 pb-4 pt-4">
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-gray-200" />
            <div className="h-3 w-11/12 rounded bg-gray-200" />
            <div className="h-3 w-2/3 rounded bg-gray-200" />
          </div>
          {/* key question block */}
          <div className="rounded-r-md py-2.5 px-3.5 space-y-2" style={{ background: 'var(--m2-mint)', borderLeft: '3px solid var(--m2-brand)' }}>
            <div className="h-2.5 w-24 rounded bg-white/60" />
            <div className="h-3 w-3/4 rounded bg-white/60" />
          </div>
          {/* objectives grid */}
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2 rounded-md px-2.5 py-1.5" style={{ background: 'var(--m2-line-soft)' }}>
                <div className="w-[18px] h-[18px] rounded-full bg-gray-200 shrink-0" />
                <div className="h-3 w-full rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* call-to-action bubble */}
    <div className="flex justify-start mb-4">
      <div className="w-[60%] sm:w-[50%] rounded-lg px-3 py-2.5 sm:px-4 bg-white border border-gray-200 space-y-2">
        <div className="h-3 w-full rounded bg-gray-200" />
        <div className="h-3 w-1/2 rounded bg-gray-200" />
      </div>
    </div>
  </div>
)

// First coach message — rendered as stacked bubbles: welcome, scenario brief, call to action.
// Relies on the --m2-* CSS variables, which are scoped to the `.module2-coach` class — render this inside
// an element carrying that class (Module2 already does; ChatDetail wraps it explicitly).
const CoachMessageCard: React.FC<{ payload: CoachMessagePayload }> = ({ payload }) => (
  <>
    {payload.message && <CoachBubble content={payload.message} />}
    {payload.welcomeMessage && <CoachBubble content={payload.welcomeMessage} />}
    <CoachScenarioCard payload={payload} />
    {payload.callToAction && <CoachBubble content={payload.callToAction} />}
  </>
)

export default CoachMessageCard
